import { useEffect, useRef } from "react";
import { gsap, SplitText, prefersReducedMotion } from "@/lib/gsapSetup";
import { about } from "@/data/siteContent";
import Img from "@/components/Img";

export default function About() {
  const ref = useRef(null);

  useEffect(() => {
    const reduced = prefersReducedMotion();
    const ctx = gsap.context(() => {
      const stmt = ref.current.querySelector("[data-statement]");
      if (SplitText && stmt && !reduced) {
        const split = new SplitText(stmt, { type: "words" });
        gsap.set(split.words, { opacity: 0.15 });
        gsap.to(split.words, {
          opacity: 1,
          stagger: 0.04,
          ease: "none",
          scrollTrigger: {
            trigger: stmt,
            start: "top 75%",
            end: "top 30%",
            scrub: true,
          },
        });
      }

      gsap.from(ref.current.querySelectorAll("[data-reveal]"), {
        opacity: 0,
        y: reduced ? 0 : 40,
        duration: 1,
        ease: "power3.out",
        stagger: 0.12,
        scrollTrigger: { trigger: ref.current, start: "top 70%" },
      });

      if (!reduced) {
        gsap.to(ref.current.querySelector("[data-portrait]"), {
          yPercent: 12,
          ease: "none",
          scrollTrigger: {
            trigger: ref.current,
            start: "top bottom",
            end: "bottom top",
            scrub: true,
          },
        });
      }
    }, ref);
    return () => ctx.revert();
  }, []);

  return (
    <section
      id="about"
      ref={ref}
      className="border-y border-white/10 bg-[#080808]"
      data-testid="about-section"
    >
      <div className="mx-auto max-w-[1400px] px-6 py-20 md:px-10 md:py-32">
        <p
          className="mb-8 font-accent text-lg italic text-white/60"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          {about.eyebrow}
        </p>

        <p
          data-statement
          className="max-w-5xl font-display text-3xl leading-[1.2] tracking-tight text-white sm:text-4xl lg:text-[3.4vw]"
          style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
        >
          {about.statement}
        </p>

        <div className="mt-16 grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="overflow-hidden rounded-[1.5rem] border border-white/10 bg-[#050505]">
              <Img
                data-portrait
                src={about.portrait}
                alt="Klyne Labs logo"
                width={1100}
                height={1100}
                sizes="(max-width: 1024px) 100vw, 40vw"
                className="h-[420px] w-full object-contain md:h-[520px]"
              />
            </div>
          </div>

          <div className="flex flex-col justify-center lg:col-span-6 lg:col-start-7">
            {about.body.map((p, i) => (
              <p
                key={i}
                data-reveal
                className="mb-6 font-body text-base leading-[1.75] text-white/75 md:text-lg"
              >
                {p}
              </p>
            ))}

            <div data-reveal className="mt-6 grid grid-cols-2 gap-6">
              {about.team.map((m) => (
                <div key={m.name} className="flex items-center gap-4">
                  <Img
                    src={m.image}
                    alt={m.name}
                    width={112}
                    height={112}
                    widths={[112, 224]}
                    sizes="56px"
                    className="h-14 w-14 rounded-full object-cover grayscale"
                  />
                  <div>
                    <div className="text-sm font-medium text-white">{m.name}</div>
                    <div className="text-xs text-white/60">{m.role}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
